<script lang="ts">
  import { onMount, tick } from 'svelte';
  import './app.css';
  import { QueryClientProvider } from '@tanstack/svelte-query';
  import { getQueryClient } from '$lib/state/queryClient';
  import { ensureSession, logout as apiLogout } from '$lib/api/auth';
  import { fetchMe } from '$lib/api/me';
  import { session } from '$lib/state/session.svelte';
  import { route } from '$lib/state/route.svelte';
  import { getErrorMessage } from '$lib/api/client';
  import Login from '$lib/components/Login.svelte';
  import Toast from '$lib/components/ui/Toast.svelte';
  import Sidebar from '$lib/components/shell/Sidebar.svelte';
  import TopBar from '$lib/components/shell/TopBar.svelte';
  import PipelineBoard from '$lib/components/pipeline/PipelineBoard.svelte';
  import NewQuoteModal from '$lib/components/pipeline/NewQuoteModal.svelte';
  import QuoteDetail from '$lib/components/quote/QuoteDetail.svelte';
  import ProjectsPage from '$lib/components/projects/ProjectsPage.svelte';
  import AdminPage from '$lib/components/admin/AdminPage.svelte';
  import SearchPage from '$lib/components/search/SearchPage.svelte';
  import PublicShareView from '$lib/components/share/PublicShareView.svelte';
  import CatalogueLibrary from '$lib/components/admin/catalog/CatalogueLibrary.svelte';
  import PrivateFileView from '$lib/components/admin/catalog/PrivateFileView.svelte';

  const queryClient = getQueryClient();

  let isAuthenticated = $state(false);
  let isBootstrapping = $state(true);
  let bootError = $state('');
  let search = $state('');
  let showPicker = $state(false);
  let mobileNavOpen = $state(false);

  const tab = $derived(route.current.tab);

  function normalizeRouteAfterAuth() {
    if (session.me?.user.role === 'catalogue_manager' && route.current.tab !== 'catalogue') {
      route.replace({ tab: 'catalogue' });
    } else if (route.current.tab === 'admin' && !session.isAdmin) {
      route.replace({ tab: 'pipeline' });
    } else {
      route.replace(route.current);
    }
  }

  async function bootstrap() {
    isBootstrapping = true;
    bootError = '';
    try {
      const me = await fetchMe();
      session.setMe(me);
      isAuthenticated = true;
      normalizeRouteAfterAuth();
    } catch (err) {
      bootError = getErrorMessage(err);
      isAuthenticated = false;
    } finally {
      isBootstrapping = false;
    }
  }

  async function checkAuth() {
    const ok = await ensureSession();
    if (ok) {
      await bootstrap();
    } else {
      isBootstrapping = false;
    }
  }

  // Allowances are server-side user attributes, not token claims. Refresh the
  // actor record when returning to the app and periodically while it remains
  // open so the UI's allowance label follows an admin update. A transient
  // failure deliberately leaves the current session/UI intact; request-time
  // authorization remains authoritative on the backend.
  let revalidatingMe = false;
  async function revalidateMe() {
    if (!isAuthenticated || revalidatingMe) return;
    revalidatingMe = true;
    try {
      session.setMe(await fetchMe());
      normalizeRouteAfterAuth();
    } catch {
      // Do not log out a live session on a transient network/API failure.
    } finally {
      revalidatingMe = false;
    }
  }

  onMount(() => {
    const onFocus = () => void revalidateMe();
    window.addEventListener('focus', onFocus);
    const interval = window.setInterval(() => void revalidateMe(), 60_000);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.clearInterval(interval);
    };
  });

  async function logout() {
    if (!route.canNavigate()) return;
    await apiLogout();
    session.clear();
    isAuthenticated = false;
    queryClient.clear();
    await tick(); // Dispose the confirmed editor guard before resetting the route.
    route.replace({ tab: 'pipeline' });
  }

  // Safety net beyond the post-login redirect above: a non-admin who edits
  // the hash directly (or hits Back onto a stale entry) into #/admin — every
  // admin query 403s server-side anyway, but bounce to the pipeline rather
  // than render a page that's all error states.
  $effect(() => {
    if (isAuthenticated && ((route.current.tab === 'admin' && !session.isAdmin) ||
      (session.me?.user.role === 'catalogue_manager' && route.current.tab !== 'catalogue'))) {
      route.replace({ tab: session.me?.user.role === 'catalogue_manager' ? 'catalogue' : 'pipeline' });
    }
  });

  checkAuth();

  const pageTitle = $derived(tab === 'projects' ? 'Projects' : tab === 'catalogue' ? 'Catalogue' : tab === 'admin' ? 'Admin' : tab === 'search' ? 'Search' : session.isAdmin ? 'Pipeline' : 'My pipeline');
  // Every admin sub-page (Users, Verticals, Catalog) renders its own
  // title + description above its content — an admin-wide subtitle here
  // would just be a second, competing page title (see e.g. CatalogPage's
  // "Product Catalog — N products …" header). Leave it blank, same as the
  // quotations tab already does for QuoteDetail's own header.
  const pageSub = $derived(
    tab === 'admin' || tab === 'search' || tab === 'catalogue' || tab === 'projects'
      ? ''
      : session.isAdmin
        ? 'All quotations across every vertical'
        : `Your quotations across ${session.me?.verticals.length ?? 0} vertical${session.me?.verticals.length === 1 ? '' : 's'}`
  );
</script>

<svelte:head>
  <title>ProQuote</title>
</svelte:head>

<Toast />

{#if route.current.tab === 'share'}
  <!-- Public, unauthenticated view — deliberately rendered before the login
       gate below: a client following a share link has no account and must
       never be bounced to the login screen. -->
  <PublicShareView token={route.current.token} />
{:else if isBootstrapping}
  <!-- text-dark-gray (not Tailwind's fixed gray-500, which fails AA against
       the dark theme's page background) -->
  <div class="min-h-screen flex items-center justify-center text-dark-gray">Loading...</div>
{:else if !isAuthenticated}
  <Login onAuthenticated={bootstrap} />
{:else}
  <QueryClientProvider client={queryClient}>
    <div class="flex h-screen w-full overflow-hidden bg-bg font-body text-toyama-heavy-carbon">
      <Sidebar onLogout={logout} open={mobileNavOpen} onNavigate={() => (mobileNavOpen = false)} />
      <div class="flex-1 min-w-0 flex flex-col overflow-hidden">
        <TopBar
          title={tab === 'quotations' ? 'Quotation' : pageTitle}
          subtitle={tab === 'quotations' ? '' : pageSub}
          showBack={tab === 'quotations'}
          onBack={() => route.push({ tab: 'pipeline' })}
          search={tab === 'pipeline' ? search : undefined}
          onSearch={tab === 'pipeline' ? (v) => (search = v) : undefined}
          onNewQuote={tab !== 'admin' && tab !== 'catalogue' && session.me?.user.role !== 'catalogue_manager' ? () => (showPicker = true) : undefined}
          onMenuClick={() => (mobileNavOpen = !mobileNavOpen)}
        />

        {#if bootError}
          <div class="m-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{bootError}</div>
        {/if}

        {#if tab === 'pipeline'}
          <PipelineBoard {search} onOpen={(id) => route.push({ tab: 'quotations', id })} />
        {:else if tab === 'projects'}
          <ProjectsPage />
        {:else if tab === 'quotations' && route.current.tab === 'quotations' && route.current.id}
          <QuoteDetail quotationId={route.current.id} />
        {:else if tab === 'admin' && session.isAdmin}
          <AdminPage />
        {:else if tab === 'catalogue'}
          {#if route.current.tab === 'catalogue' && route.current.file}
            {#key `${route.current.file.asset}:${route.current.file.revision}`}<PrivateFileView {...route.current.file} />{/key}
          {:else}<CatalogueLibrary />{/if}
        {:else if tab === 'search'}
          <SearchPage />
        {/if}
      </div>
    </div>

    {#if showPicker}
      <NewQuoteModal
        onClose={() => (showPicker = false)}
        onCreated={(q) => {
          showPicker = false;
          queryClient.invalidateQueries({ queryKey: ['pipeline-quotations'] });
          queryClient.invalidateQueries({ queryKey: ['project-quotations'] });
          route.push({ tab: 'quotations', id: q.id });
        }}
      />
    {/if}
  </QueryClientProvider>
{/if}
